import type { CompareModel } from "../../../lib/stageViewModels";
import { ScrollCenter } from "./McqBars";
import { useGrowIn } from "./useGrowIn";

const SEGMENT_CLS = ["bg-rc-berry", "bg-rc-lime", "bg-rc-paper/70", "bg-rc-paper/35"];

export function CompareUnavailable({ loading }: { loading: boolean }) {
  return (
    <div className="h-full flex items-center justify-center text-center">
      <p className="text-[15px] text-rc-paper/50 max-w-md leading-[1.5]">
        {loading ? "Vergleich wird geladen …" : "Vergleich erscheint, sobald jede Gruppe mindestens drei Antworten hat."}
      </p>
    </div>
  );
}

export function CompareLegend({ compare }: { compare: CompareModel }) {
  return (
    <div className="flex-shrink-0 flex flex-wrap items-center gap-x-6 gap-y-2 mb-6">
      {compare.segments.map((s, i) => (
        <span key={s.key} className="flex items-center gap-2 text-[12px] font-semibold tracking-wide text-rc-paper/80">
          <span aria-hidden className={`w-3 h-3 rounded-full ${SEGMENT_CLS[i % SEGMENT_CLS.length]}`} />
          {s.label} <span className="text-rc-paper/45 tabular-nums font-medium">{s.respondents} Befragte</span>
        </span>
      ))}
    </div>
  );
}

/** One row per answer option; inside, one bar per segment (% of that segment's respondents). */
export function CompareBars({ compare, options, loading }: { compare: CompareModel; options: Array<{ key: string; label: string }>; loading: boolean }) {
  const { grown, reduced, delay } = useGrowIn();
  if (!compare.available) return <CompareUnavailable loading={loading} />;
  const compact = options.length * compare.segments.length > 10;
  return (
    <div className="flex flex-col h-full text-rc-paper">
      <CompareLegend compare={compare} />
      <ScrollCenter as="ul" className={compact ? "gap-3" : "gap-5"}>
        {options.map((opt, oi) => (
          <li key={opt.key} className="flex flex-col gap-1.5">
            <span className="font-semibold leading-tight" style={{ fontSize: compact ? "clamp(0.85rem, 1.1vw, 1.1rem)" : "clamp(0.95rem, 1.3vw, 1.3rem)" }}>{opt.label}</span>
            {compare.segments.map((s, si) => {
              const n = s.values[opt.key] ?? 0;
              const pct = s.respondents === 0 ? 0 : Math.round((n / s.respondents) * 100);
              return (
                <div key={s.key} className="flex items-center gap-3">
                  <div className={`flex-1 ${compact ? "h-2" : "h-2.5"} rounded-full bg-rc-paper/[0.08] overflow-hidden`}>
                    <div className={`h-full rounded-full ${SEGMENT_CLS[si % SEGMENT_CLS.length]} ${reduced ? "" : "transition-[width] duration-700 ease-out"}`} style={{ width: grown ? `${pct}%` : "0%", transitionDelay: delay(oi * compare.segments.length + si) }} aria-label={`${s.label}, ${opt.label}: ${pct} Prozent`} />
                  </div>
                  <span className="w-20 text-right tabular-nums text-[13px]"><span className="font-bold">{pct}%</span> <span className="text-rc-paper/45">{n}</span></span>
                </div>
              );
            })}
          </li>
        ))}
      </ScrollCenter>
    </div>
  );
}
